/**
 * Detect whether the app is running inside the Electron desktop shell.
 * The shell exposes a `window.efacture` bridge via contextBridge in
 * apps/desktop/src/preload.ts; in a regular browser the namespace is
 * undefined.
 *
 * Use to hide online-only features in the on-premise/offline build —
 * e.g. BCEAO PI-SPI relies on inbound webhooks the desktop app cannot
 * receive without a public callback URL.
 */
/** Result of a desktop IPC call: the value, or a French message. */
export type DesktopIpcResult<T> = { ok: true; value: T } | { ok: false; error: string };

/** The site MCF agent as the desktop reports it (apps/desktop/src/mcf-agent-ipc.ts). */
export interface DesktopMcfAgentState {
  platform: string;
  /** False when this installation carries no agent. */
  bundled: boolean;
  /** MCF_MODE, MCF_PROTOCOL and MCF_AGENT_FALLBACK the embedded API runs with. */
  mcf: { mode: string; protocol: string; fallback: string | null };
  link: { agentId: string; linkPort: number; fingerprint: string; linkedAt: string } | null;
  linkError: string | null;
  service: {
    installed: boolean;
    running: boolean;
    pid: number | null;
    config: { configured: boolean; upstreams: string[]; mcfProtocol: string; serialPort: string } | null;
    health: {
      mcfConnected: boolean;
      mcfPort: string | null;
      upstreams: { name: string; connected: boolean }[];
    } | null;
  } | null;
  serviceError: string | null;
  /** French notice when the installed agent copy is older than the application. */
  runtimeNotice: string | null;
}

export interface DesktopMcfAgentBridge {
  state(): Promise<DesktopIpcResult<DesktopMcfAgentState>>;
  link(accessToken: string): Promise<DesktopIpcResult<unknown>>;
  unlink(accessToken: string): Promise<DesktopIpcResult<unknown>>;
  connectCloud(input: { url: string; token: string; tlsFingerprint?: string }): Promise<DesktopIpcResult<unknown>>;
  disconnectCloud(): Promise<DesktopIpcResult<unknown>>;
}

/** A printer of this computer; `name` is the system name a print job targets. */
export interface DesktopPrinter {
  name: string;
  displayName: string;
  isDefault: boolean;
}

/** The till-ticket printer of this computer; a null `deviceName` prints through the dialog. */
export interface DesktopTicketPrinterSettings {
  deviceName: string | null;
  widthMm: 58 | 80;
}

/**
 * The desktop's silent ticket printing (#929 §7.3). `print` resolves with
 * the outcome the OS spooler reported for the job; `timedOut` marks a silent
 * job the desktop got no outcome for within its bound, which may still print.
 * `onDialogOpen` runs when the desktop reports the job's print dialog open.
 */
export interface DesktopTicketPrinterBridge {
  listPrinters(): Promise<DesktopPrinter[]>;
  getSettings(): Promise<DesktopTicketPrinterSettings>;
  saveSettings(settings: DesktopTicketPrinterSettings): Promise<void>;
  print(
    html: string,
    widthMm: 58 | 80,
    onDialogOpen?: () => void
  ): Promise<{ success: boolean; failureReason?: string; timedOut?: boolean }>;
}

declare global {
  interface Window {
    efacture?: {
      isOffline?: boolean;
      platform?: string;
      getApiUrl?: () => Promise<string>;
      getAppVersion?: () => Promise<string>;
      mcfAgent?: DesktopMcfAgentBridge;
      ticketPrinter?: DesktopTicketPrinterBridge;
    };
  }
}

/** The desktop's MCF agent bridge, or null outside the desktop shell. */
export function desktopMcfAgent(): DesktopMcfAgentBridge | null {
  return typeof window !== 'undefined' ? (window.efacture?.mcfAgent ?? null) : null;
}

/** The desktop's ticket printer bridge, or null outside the desktop shell. */
export function desktopTicketPrinter(): DesktopTicketPrinterBridge | null {
  return typeof window !== 'undefined' ? (window.efacture?.ticketPrinter ?? null) : null;
}

export function isDesktopApp(): boolean {
  return typeof window !== 'undefined' && window.efacture?.isOffline === true;
}

// ─── Desktop API base URL ───
//
// Under the Electron shell the page is loaded via file://, so the default
// '/api' base is meaningless. The main process exposes the embedded API's
// URL (e.g. http://127.0.0.1:3001) through the async `get-api-url` IPC.
// `initDesktopApi()` resolves it ONCE before the app renders (called from
// main.tsx) and stores it so `getDesktopApiBaseUrl()` can be read
// synchronously by the API client.

let desktopApiBaseUrl: string | null = null;

/**
 * Resolve and cache the embedded API base URL when running in the
 * desktop shell. No-op (resolves immediately) in a regular browser.
 * Never rejects — on failure the client falls back to its defaults.
 */
export async function initDesktopApi(): Promise<void> {
  if (typeof window === 'undefined' || typeof window.efacture?.getApiUrl !== 'function') {
    return;
  }
  try {
    const apiUrl = await window.efacture.getApiUrl();
    if (apiUrl) {
      // The API serves its routes under /api (e.g. /api/auth/login).
      desktopApiBaseUrl = `${apiUrl.replace(/\/$/, '')}/api`;
    }
  } catch {
    // IPC failure — leave null; the API client falls back to '/api'.
  }
}

/** Cached desktop API base URL, or null outside the desktop shell. */
export function getDesktopApiBaseUrl(): string | null {
  return desktopApiBaseUrl;
}
